import { QUERIES } from '@repo/ui/queries';
import { themeStyle } from '@repo/ui/theming';
import styled from 'styled-components';

export const HORIZONTAL_PADDING_MOBILE = '12px';

export const Anchor = styled.a`
  color: ${themeStyle.varBlueMain};
  text-decoration-line: none;

  &:hover {
    text-decoration-line: underline;
  }
`;

export const Title = styled.h1<{ color?: string; large?: boolean }>`
  color: ${({ color }): string => color ?? themeStyle.varInkMain};
  font-weight: ${themeStyle.fontBold};
  font-size: ${(props): string => (props.large ? '34px' : '24px')};
  line-height: 100%;
  margin: 0 0 14px;
`;

export const Wrapper = styled.div<{ minHeight: string; background?: string }>`
  background: ${(props): string =>
    props.background || themeStyle.varBackgroundBase};
  ${(props): string =>
    props.minHeight ? `min-height:${props.minHeight};` : ''}
`;

export const ContentWrapper = styled.div<{
  background?: string;
  minHeight?: string;
  isNewDesignEnabled?: boolean;
}>`
  padding: ${({ isNewDesignEnabled }) =>
    isNewDesignEnabled
      ? `0 var(--spacing-24) 50px`
      : `0 ${HORIZONTAL_PADDING_MOBILE} 50px`};
  ${QUERIES.laptopAndUp} {
    padding: 0 28px 28px;
  }

  background: ${(props): string => props.background ?? 'unset'};
  ${(props): string =>
    props.minHeight ? `min-height:${props.minHeight};` : ''}
`;

export const Container = styled.div<{
  useGradient?: boolean;
  smallGradient?: boolean;
  gradient?: string;
  isNewDesignEnabled?: boolean;
}>`
  max-width: 2560px;
  width: 100%;
  margin: 0 auto;

  ${({ useGradient, smallGradient, gradient, isNewDesignEnabled }): string =>
    !isNewDesignEnabled && (useGradient || gradient)
      ? `
    background: ${gradient ? gradient : themeStyle.varBackgroundGradientHalf};
    background-size: 100% ${smallGradient ? '200px' : '530px'};
    background-repeat: no-repeat;
  `
      : ''}
`;

export const TextWrapper = styled.div<{
  textAlign?: CSSStyleDeclaration['textAlign'];
}>`
  color: ${themeStyle.varInkMain};
  text-align: ${({ textAlign }): string => textAlign || 'unset'};
  font-size: ${themeStyle.fontSize14};
  line-height: 22px;
  font-weight: ${themeStyle.fontMedium};
  color: ${themeStyle.varInkMain};

  & p,
  ul {
    margin: 0;
    margin-bottom: 26px;
  }

  & h2 {
    font-size: ${themeStyle.fontSize14};
    font-weight: ${themeStyle.fontBold};
    line-height: 22px;
    margin: 0;
  }
`;

export const Card = styled.div<{
  padding: string;
  background: string;
  border?: string;
}>`
  width: 100%;
  max-width: 660px;
  padding: ${({ padding }): string => padding};
  background: ${({ background }): string => background};
  box-shadow: ${themeStyle.varShadowSoft};
  border-radius: ${themeStyle.radiusLarge};
  border: ${({ border }): string => border ?? 'unset'};
`;

export const HighlightWrapper = styled.div`
  padding: 48px;
  background: ${themeStyle.varBackgroundAlt};
  border-radius: ${themeStyle.radiusLarge};
`;

export const NumberedList = styled.ol`
  color: ${themeStyle.varInkMain};
  counter-reset: numbered-list-counter;
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    counter-increment: numbered-list-counter;
    padding-bottom: 16px;
    font-size: ${themeStyle.fontSize14};
    line-height: 22px;
    font-weight: ${themeStyle.fontMedium};

    &:last-of-type {
      padding-bottom: 0;
    }

    &:before {
      content: counter(numbered-list-counter) '.';
      font-size: 24px;
      font-weight: ${themeStyle.fontExtraBold};
      color: ${themeStyle.varInkMain};
      padding-right: 12px;
    }
  }
`;

export const BulletList = styled.ul`
  color: ${themeStyle.varInkMain};

  li {
    font-size: ${themeStyle.fontSize14};
    line-height: 22px;
    font-weight: ${themeStyle.fontMedium};
    padding-bottom: 8px;

    &:last-of-type {
      padding-bottom: 0;
    }
  }
`;

export const Content = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;

  max-width: 660px;
  margin: 0 auto;
`;

export const Image = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: ${themeStyle.radiusSmall};
`;

export interface NotifyBoxTheme {
  padding?: string;
  margin?: string;
}

export const NotifyBox = styled.div<NotifyBoxTheme>`
  padding: ${({ padding }): string => (padding ? padding : '12px')};
  margin: ${({ margin }): string => (margin ? margin : '0')};
  background-color: ${themeStyle.varBlueLight};
  border-radius: ${themeStyle.radiusLarge};

  ${QUERIES.largeTabletAndUp} {
    padding: ${({ padding }): string => (padding ? padding : '24px')};
  }
`;
